import { useTheme } from '@mui/material';
import MuiMenu, { MenuProps as MuiMenuProps } from '@mui/material/Menu';
import React, {
  cloneElement,
  forwardRef,
  ReactElement,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';

export interface MenuHandler {
  onClose?: () => void;
}

interface MenuProps extends Omit<MuiMenuProps, 'open' | 'onClose'> {
  menuTrigger: ReactElement<{
    onClick?: React.MouseEventHandler;
    style?: React.CSSProperties;
  }>;
  // Opt-in: draws a border around the trigger while its menu is open. Every
  // other consumer of this shared component (status dropdowns, filters,
  // export, row action menus, ...) relies on the trigger's own default
  // appearance, so this stays off unless a caller explicitly asks for it.
  highlightTriggerWhenOpen?: boolean;
  onOpenStateChanged?: (isOpen: boolean) => void;
  preventCloseCondition?: boolean;
}

const Menu = forwardRef<MenuHandler, MenuProps>(function Menu(
  {
    highlightTriggerWhenOpen,
    menuTrigger,
    onOpenStateChanged,
    preventCloseCondition,
    sx,
    ...rest
  },
  ref,
) {
  const theme = useTheme();
  const [anchor, setAnchor] = useState<Element | null>(null);
  const isOpen = Boolean(anchor);
  const rootSx = (sx && '& .MuiPaper-root' in sx ? sx['& .MuiPaper-root'] : {}) as object;

  useImperativeHandle(
    ref,
    () => ({
      onClose() {
        setAnchor(null);
      },
    }),
    [],
  );

  useEffect(() => {
    onOpenStateChanged?.(isOpen);
  }, [isOpen, onOpenStateChanged]);

  return (
    <>
      {cloneElement(menuTrigger, {
        onClick: (event: React.MouseEvent) => {
          event.stopPropagation();
          setAnchor(event.currentTarget);
        },
        ...(highlightTriggerWhenOpen
          ? {
              // An inline `border` (not a box-shadow ring) REPLACES whatever
              // border the trigger already draws (e.g. "Add condition"'s
              // dashed gray one) the same way MUI swaps an outlined input's
              // border color on focus, rather than stacking a second ring on
              // top of the existing border.
              style: {
                ...menuTrigger.props.style,
                border: isOpen ? `2px solid ${theme.palette.primary.main}` : undefined,
                borderRadius: 4,
              },
            }
          : {}),
      })}

      <MuiMenu
        anchorEl={anchor}
        onClose={(event: React.MouseEvent) => {
          event.stopPropagation();

          if (!preventCloseCondition) {
            setAnchor(null);
          }
        }}
        open={isOpen}
        sx={{
          ...sx,
          '& .MuiPaper-root': {
            border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
            boxShadow: '0px 1px 10px 0px rgba(60, 64, 67, 0.15)',
            minWidth: '150px',
            mt: '3px',
            ...rootSx,
          },
          '& .MuiItem-root': { px: theme.spacing(1.5) },
        }}
        {...rest}
      />
    </>
  );
});

export default Menu;
